<template>
  <AppLayout
    page-title="My Dispatch"
    page-subtitle="Your callouts and pad assignments"
  >
    <div class="space-y-6 pt-4">
      <div>
        <h1 class="text-2xl font-bold text-white">My Dispatch</h1>
        <p class="text-slate-400 text-sm mt-1">Confirm callouts and track where you’re working.</p>
      </div>

      <p v-if="error" class="text-red-400">{{ error }}</p>
      <p v-if="message" class="text-emerald-400">{{ message }}</p>

      <div v-if="loading" class="text-slate-400 py-10 text-center">Loading…</div>
      <div v-else-if="!assignments.length" class="panel text-slate-400 text-center py-12">
        No dispatch assignments yet. Your company will call you out from the dispatch board.
      </div>

      <div v-else class="space-y-4">
        <div v-for="a in assignments" :key="a.id" class="panel">
          <div class="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p class="text-xs uppercase tracking-wide text-slate-500">{{ a.priority || 'normal' }} · {{ String(a.status || '').replace('_', ' ') }}</p>
              <h2 class="text-lg font-semibold text-white mt-1">{{ a.title || a.jobTitle || 'Dispatch' }}</h2>
              <p class="text-slate-400 text-sm mt-1">
                {{ a.date || a.assignmentDate }} · {{ a.startTime || '—' }}–{{ a.endTime || '—' }} · {{ a.siteName || 'Site TBD' }}
              </p>
              <p v-if="a.fleetUnit || a.equipmentName" class="text-xs text-slate-500 mt-1">
                {{ [a.fleetUnit, a.equipmentName].filter(Boolean).join(' · ') }}
              </p>
              <p v-if="a.calloutMessage" class="text-sm text-amber-200/90 mt-3 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2">
                {{ a.calloutMessage }}
              </p>
            </div>
            <div class="flex flex-wrap gap-2">
              <button
                v-if="['draft','notified'].includes(a.status)"
                class="btn-primary"
                :disabled="busy === a.id"
                @click="act(a.id, 'confirm')"
              >Confirm</button>
              <button
                v-if="['confirmed','notified'].includes(a.status)"
                class="btn-secondary"
                :disabled="busy === a.id"
                @click="act(a.id, 'start')"
              >Start Shift</button>
              <button
                v-if="a.status === 'in_progress'"
                class="btn-primary"
                :disabled="busy === a.id"
                @click="act(a.id, 'complete')"
              >Complete</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import AppLayout from '@/components/layout/AppLayout.vue'
import { buildApiUrl } from '@/config/api'

const route = useRoute()
const dispatchApi = computed(() =>
  route.path.startsWith('/dispatch') || localStorage.getItem('user_type') === 'contractor'
    ? 'contractor/dispatch'
    : 'employee/dispatch'
)
const loading = ref(true)
const busy = ref<number | null>(null)
const error = ref('')
const message = ref('')
const assignments = ref<any[]>([])

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`, 'Content-Type': 'application/json' }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const res = await fetch(buildApiUrl(dispatchApi.value), { headers: authHeaders() })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load')
    assignments.value = (data.assignments || []).map((row: any) => ({
      ...row,
      title: row.title || row.jobTitle || 'Dispatch',
      date: row.date || row.assignmentDate,
      status: row.status || 'notified',
      priority: row.priority || 'normal',
    }))
  } catch (e: any) {
    error.value = e.message || 'Failed to load'
  } finally {
    loading.value = false
  }
}

async function act(id: number, action: string) {
  busy.value = id
  error.value = ''
  message.value = ''
  try {
    const res = await fetch(buildApiUrl(`${dispatchApi.value}/${id}/${action}`), {
      method: 'POST',
      headers: authHeaders(),
      body: '{}',
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Update failed')
    message.value = `Assignment ${action}ed`
    await load()
  } catch (e: any) {
    error.value = e.message || 'Update failed'
  } finally {
    busy.value = null
  }
}

onMounted(load)
</script>

<style scoped>
.panel { @apply bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5; }
.btn-primary { @apply inline-flex items-center px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium disabled:opacity-50; }
.btn-secondary { @apply inline-flex items-center px-4 py-2 rounded-xl bg-slate-700/60 border border-slate-600 text-slate-200 disabled:opacity-50; }
</style>
